import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { DocumentTitle, IfPermitted, PageHeader, Spinner } from "components/common";
import withParams from "routing/withParams";
import { useStore } from "stores/connect";
import { Button, Col, Row } from "components/bootstrap";
import ForwarderDocs from "forwarder/util/ForwarderDocs";
import { LICENSE_SUBJECTS } from "license/constants";
import LicenseCheck from "license/LicenseCheck";
import ForwarderInputsManagement from "./inputs-list/ForwarderInputsManagement";
import InputProfilesForwarderList from "./input-profiles/InputProfilesForwarderList";
import InputProfileAssignForm from "./input-profiles/InputProfileAssignForm";
import InputProfilesStore, { InputProfilesActions } from "./stores/InputProfilesStore";
import ForwarderInputsStore, { ForwarderInputsActions } from "./stores/ForwarderInputsStore";
import ForwardersStore, { ForwardersActions } from "./stores/ForwardersStore";
import InputProfileDetailsComponent from "./input-profiles/InputProfileDetailsComponent";
import withIsPermitted from "./util/withIsPermitted";
import ForwardersContext from "./forwarders/ForwardersContext";
import PaginatedForwarderListProvider from "./forwarders/PaginatedForwarderListProvider";
const StyledH2 = styled.h2`
  margin-bottom: 20px;
`;
const requiredPermissions = ({ params }) => [
  "forwarders:read",
  `inputprofiles:read:${params.inputProfileId}`,
  "forwarderinputs:read"
];
const InputProfilePage = ({ params }) => {
  const { t } = useTranslation("enterprise_forwarder");
  const { inputProfileId } = params;
  const { inputProfile } = useStore(InputProfilesStore);
  const { forwarderInputs, pagination } = useStore(ForwarderInputsStore);
  const { all: forwarders } = useStore(ForwardersStore);
  const [showForwarderList, setShowForwarderList] = useState(true);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const _saveProfileAssignment = (updatedForwarders) => {
    const toBeUpdatedForwarders = [];
    updatedForwarders.forEach((uf) => {
      const forwarder = forwarders.find((f) => f.id === uf.forwarderId);
      if (forwarder) {
        toBeUpdatedForwarders.push({ ...forwarder, input_profile_id: uf.inputProfileId });
      }
    });
    ForwardersActions.updates(toBeUpdatedForwarders);
  };
  const _reloadInputs = () => {
    ForwarderInputsActions.list({ inputProfileId });
  };
  const _toggleForwarderList = (isShowing) => {
    setShowForwarderList(isShowing);
  };
  const _handleQuery = (page, pageSize, query = "") => {
    ForwarderInputsActions.list({
      inputProfileId,
      page,
      pageSize,
      query
    });
  };
  const assignedForwarders = useMemo(
    () => (forwarders || []).filter((f) => f.input_profile_id === inputProfileId),
    [forwarders, inputProfileId]
  );
  const _loadInputProfile = useCallback(() => {
    InputProfilesActions.get(inputProfileId);
  }, [inputProfileId]);
  useEffect(() => {
    _loadInputProfile();
    ForwarderInputsActions.list({ inputProfileId });
    ForwardersActions.listAll();
  }, [inputProfileId, _loadInputProfile]);
  if (!inputProfile) {
    return /* @__PURE__ */ React.createElement(Spinner, null);
  }
  return /* @__PURE__ */ React.createElement(DocumentTitle, { title: t("input_profile_page.document_title") }, /* @__PURE__ */ React.createElement(
    PageHeader,
    {
      title: t("input_profile_page.title", { title: inputProfile.title }),
      documentationLink: {
        title: t("common.input_profile_documentation"),
        path: ForwarderDocs.INPUT_PROFILE
      }
    },
    /* @__PURE__ */ React.createElement("span", null, t("input_profile_page.description", { title: inputProfile.title }))
  ), /* @__PURE__ */ React.createElement(
    LicenseCheck,
    {
      licenseSubject: LICENSE_SUBJECTS.forwarder,
      displayWarningContainer: true,
      featureName: "Forwarder",
      bsStyle: "danger"
    }
  ), /* @__PURE__ */ React.createElement(Row, { className: "content" }, /* @__PURE__ */ React.createElement(InputProfileDetailsComponent, { inputProfile, onUpdate: _loadInputProfile })), /* @__PURE__ */ React.createElement(Row, { className: "content" }, /* @__PURE__ */ React.createElement(Col, { md: 12 }, /* @__PURE__ */ React.createElement("h2", null, t("input_profile_page.inputs_heading")), forwarderInputs[inputProfileId] && /* @__PURE__ */ React.createElement(
    ForwarderInputsManagement,
    {
      inputs: forwarderInputs[inputProfileId],
      inputProfile,
      enableEdit: true,
      pagination,
      onQueryChange: _handleQuery,
      onInputChange: _reloadInputs,
      onActionChange: _toggleForwarderList
    }
  ))), /* @__PURE__ */ React.createElement(
    PaginatedForwarderListProvider,
    {
      initialPagination: { query: `input_profile_id:${inputProfileId}`, sortByField: "title", order: "asc" }
    },
    showForwarderList && /* @__PURE__ */ React.createElement(ForwardersContext.Consumer, null, ({
      forwarders: inputProfileForwarders,
      pagination: forwardersPagination,
      changePagination,
      isLoading
    }) => {
      if (inputProfileForwarders) {
        return /* @__PURE__ */ React.createElement(Row, { className: "content" }, /* @__PURE__ */ React.createElement(Col, { md: 12 }, /* @__PURE__ */ React.createElement("div", { className: "pull-right" }, /* @__PURE__ */ React.createElement(IfPermitted, { permissions: "forwarders:edit" }, /* @__PURE__ */ React.createElement(Button, { bsStyle: "primary", onClick: () => setShowAssignModal(true) }, t("input_profile_page.assign_to_forwarders")), showAssignModal && /* @__PURE__ */ React.createElement(
          InputProfileAssignForm,
          {
            forwarders,
            assignedForwarders,
            inputProfileId: inputProfile.id,
            onForwardersAssign: _saveProfileAssignment,
            showModal: showAssignModal,
            onClose: () => setShowAssignModal(false)
          }
        ))), /* @__PURE__ */ React.createElement(StyledH2, null, t("input_profile_page.forwarders_heading"), " ", /* @__PURE__ */ React.createElement("small", null, isLoading && /* @__PURE__ */ React.createElement(Spinner, { text: "" }))), inputProfileForwarders && forwardersPagination ? /* @__PURE__ */ React.createElement(
          InputProfilesForwarderList,
          {
            forwarders: inputProfileForwarders,
            pagination: forwardersPagination,
            onPageChange: changePagination
          }
        ) : /* @__PURE__ */ React.createElement(Spinner, null)));
      }
      return /* @__PURE__ */ React.createElement(Spinner, null);
    })
  ));
};
export default withParams(withIsPermitted(InputProfilePage, requiredPermissions));
